import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useAuth } from "../../../../contexts/UserContext";
import { extrairOrientacoes } from "../utils";
import { salvarOrientacoes } from "./utils";
import { Cartao, Titulo, TituloAcoes, Conteudo, ValorDestacado, BotaoPrimario, BotaoMuted, BotaoIcone, CampoTexto, Timestamp } from "../styles";
import { IconDocument, IconPencil, IconClock } from "../../../icons/IconesSistema";
import { ContainerVazio, IconeDocumentoVazio, Paragrafo, ParagrafoMuted } from "./styles";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function OrientacoesPrevias({ demanda, onSalvar, loading = false }: any) {
    const params = useParams<any>();
    const id = params.id;
    const { user } = useAuth();
    
    const podeAdicionarOrientacao = user?.profile === 'admin' || user?.profile === 'operador';
    const podeEditarOrientacao = user?.profile === 'admin' || user?.profile === 'operador';
    
    const { orientacaoPrevia } = useMemo(() => {
        if (!demanda?.orientacoes) {
            return { orientacaoPrevia: null, orientacoesCompletas: [] };
        }
        if (typeof demanda.orientacoes === 'string') {
            const texto = demanda.orientacoes.trim();
            return { orientacaoPrevia: texto || null, orientacoesCompletas: [] };
        }
        const lista = Array.isArray(demanda.orientacoes) ? demanda.orientacoes : [];
        return extrairOrientacoes(lista as unknown[]);
    }, [demanda?.orientacoes]);

    const textoNormalizado = (orientacaoPrevia && typeof orientacaoPrevia === 'string') ? orientacaoPrevia.trim() : "";
    const [editando, setEditando] = useState(false);
    const [textoAtual, setTextoAtual] = useState(textoNormalizado);
    const [textoEditado, setTextoEditado] = useState(textoNormalizado);
    const [timestampUltimaEdicao, setTimestampUltimaEdicao] = useState<Date | null>(null);

    useEffect(() => {
        const novoTexto = (orientacaoPrevia && typeof orientacaoPrevia === 'string') ? orientacaoPrevia.trim() : "";
        if (!editando) {
            setTextoAtual(novoTexto);
            setTextoEditado(novoTexto);
        }
    }, [orientacaoPrevia, editando]);

    const iniciarEdicao = () => {
        setTextoEditado(textoAtual);
        setEditando(true);
    };

    const iniciarAdicao = () => {
        setTextoAtual("");
        setTextoEditado("");
        setEditando(true);
    };

    const cancelarEdicao = () => {
        setTextoEditado(textoAtual);
        setEditando(false);
    };

    const salvarEdicao = async () => {
        await salvarOrientacoes(id, textoEditado);
        setTimestampUltimaEdicao(new Date());
        setEditando(false);
        if (onSalvar) {
            await onSalvar();
        }
    };

    const semOrientacao = textoAtual.length === 0;

    const formatarDataRelativa = (data: string | Date | null | undefined): string => {
        if (!data) return "";
        try {
            const dataFormatada = new Date(data);
            if (Number.isNaN(dataFormatada.getTime())) return "";
            return formatDistanceToNow(dataFormatada, { 
                addSuffix: true, 
                locale: ptBR 
            });
        } catch (error) {
            return "";
        }
    };

    const historicoOrientacaoPrevia = useMemo(() => {
        if (!demanda?.historico || !Array.isArray(demanda.historico)) return null;
        return demanda.historico
            .filter((h: any) => {
                const acao = (h.acao || "").toLowerCase();
                return acao.includes('orientacao previa') || acao.includes('orientação prévia');
            })
            .sort((a: any, b: any) => {
                const dataA = new Date(a.criado || 0).getTime();
                const dataB = new Date(b.criado || 0).getTime();
                return dataB - dataA;
            })?.[0];
    }, [demanda?.historico]);

    useEffect(() => {
        if (historicoOrientacaoPrevia?.criado) {
            const dataHistorico = new Date(historicoOrientacaoPrevia.criado);
            if (timestampUltimaEdicao && dataHistorico.getTime() > timestampUltimaEdicao.getTime()) {
                setTimestampUltimaEdicao(null);
            }
        }
    }, [historicoOrientacaoPrevia?.criado, timestampUltimaEdicao]);

    const dataRelativa = useMemo(() => {
        if (semOrientacao) return "";
        
        if (timestampUltimaEdicao) {
            return formatarDataRelativa(timestampUltimaEdicao);
        }
        
        if (historicoOrientacaoPrevia?.criado) {
            return formatarDataRelativa(historicoOrientacaoPrevia.criado);
        }
        
        return "";
    }, [semOrientacao, timestampUltimaEdicao, historicoOrientacaoPrevia?.criado]);

    if (loading) {
        return (
            <Cartao>
                <Titulo>
                    <Skeleton circle width={18} height={18} />
                    <Skeleton width={150} height={18} />
                </Titulo>
                <Conteudo>
                    <Skeleton width="100%" height={120} borderRadius={6} style={{ marginBottom: '16px' }} />
                </Conteudo>
            </Cartao>
        );
    }

    if (semOrientacao && !editando) {
        return (
            <Cartao>
                <Titulo>
                    <IconDocument size={18} />
                    Orientações Prévias
                </Titulo>
                <ContainerVazio>
                    <IconeDocumentoVazio>
                        <IconDocument size={28} />
                    </IconeDocumentoVazio>
                    <Paragrafo>Sem orientações prévias</Paragrafo>
                    <ParagrafoMuted>
                        {podeAdicionarOrientacao 
                            ? 'Esta demanda ainda não possui orientações prévias cadastradas. Clique no botão abaixo para adicionar.'
                            : 'Esta demanda ainda não possui orientações prévias cadastradas.'}
                    </ParagrafoMuted>
                    {podeAdicionarOrientacao && (
                        <BotaoPrimario type="button" onClick={iniciarAdicao}>
                            <BotaoIcone>
                                <IconDocument size={16} color="#fff" />
                            </BotaoIcone>
                            Adicionar Orientação Prévia
                        </BotaoPrimario>
                    )}
                </ContainerVazio>
            </Cartao>
        );
    }

    return (
        <Cartao>
            <Titulo>
                <IconDocument size={18} />
                Orientações Prévias
                {(dataRelativa || (!editando && podeEditarOrientacao)) && (
                    <TituloAcoes>
                        {dataRelativa && !editando && (
                            <Timestamp>
                                <IconClock size={14} />
                                {dataRelativa}
                            </Timestamp>
                        )}
                        {!editando && podeEditarOrientacao && (
                            <BotaoPrimario type="button" onClick={iniciarEdicao}>
                                <BotaoIcone>
                                    <IconPencil size={16} color="#fff" />
                                </BotaoIcone>
                                Editar
                            </BotaoPrimario>
                        )}
                    </TituloAcoes>
                )}
            </Titulo>
            <Conteudo>
                {editando ? (
                    <div>
                        <CampoTexto
                            value={textoEditado}
                            onChange={(evento) => setTextoEditado(evento.target.value)}
                            rows={6}
                        />
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                            <BotaoMuted type="button" onClick={cancelarEdicao}>Cancelar</BotaoMuted>
                            <BotaoPrimario type="button" onClick={salvarEdicao}>Salvar</BotaoPrimario>
                        </div>
                    </div>
                ) : (
                    <ValorDestacado style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{textoAtual || "--"}</ValorDestacado>
                )}
            </Conteudo>
        </Cartao>
    );
}
